import React, { useEffect, useState } from "react";
import { api, fmtUSD, fmtNumber } from "../lib/api";
import { ChevronRight, Home, Layers, Boxes, Cpu, Server } from "lucide-react";

const SERVICE_COLORS = {
  BigQuery: "#00579e", "Cloud SQL": "#15803d", "Compute Engine": "#9a3412",
  "Vertex AI": "#6d28d9", GKE: "#0e7490", "Cloud Storage": "#374151", "Cloud Run": "#be185d",
};

const LEVEL_META = {
  business_unit: { label: "Business Units", icon: Layers, next: "Products" },
  product:       { label: "Products",       icon: Boxes,  next: "Applications" },
  application:   { label: "Applications",   icon: Cpu,    next: "GCP Services" },
  service:       { label: "GCP Services",   icon: Server, next: null },
};

export default function HierarchyExplorer({ days = 30 }) {
  const [path, setPath] = useState([]);  // breadcrumbs: [{level, id, name}]
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (newPath) => {
    setLoading(true);
    try {
      const params = { days };
      newPath.forEach(p => {
        if (p.level === "business_unit") params.bu_id = p.id;
        if (p.level === "product")       params.product_id = p.id;
        if (p.level === "application")   params.application_id = p.id;
      });
      const d = await api.ue.hierarchy(params);
      setData(d);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(path); /* eslint-disable-next-line */ }, [days]);

  const drillInto = (row) => {
    if (!row.drillable) return;
    const next = [...path, { level: row.level, id: row.id, name: row.name }];
    setPath(next);
    load(next);
  };

  const goTo = (idx) => {
    // -1 means root
    const next = idx === -1 ? [] : path.slice(0, idx + 1);
    setPath(next);
    load(next);
  };

  const currentLevel = data?.level || "business_unit";
  const meta = LEVEL_META[currentLevel] || LEVEL_META.business_unit;

  return (
    <div className="space-y-4" data-testid="ue-hierarchy-explorer">
      {/* Breadcrumbs */}
      <div className="cs-card p-3 flex items-center gap-1 flex-wrap text-sm" data-testid="hierarchy-breadcrumbs">
        <button onClick={() => goTo(-1)} data-testid="crumb-root"
                className="flex items-center gap-1 px-2 py-1 rounded-full hover:bg-[hsl(var(--cs-tint))] text-muted-foreground hover:text-foreground transition-colors">
          <Home className="w-3.5 h-3.5" />
          <span className="text-xs">All Business Units</span>
        </button>
        {path.map((p, i) => (
          <React.Fragment key={i}>
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
            <button onClick={() => goTo(i)} data-testid={`crumb-${i}`}
                    className={`px-2 py-1 rounded-full text-xs ${i === path.length - 1 ? "bg-[#00579e] text-white font-medium" : "hover:bg-[hsl(var(--cs-tint))] text-muted-foreground hover:text-foreground"}`}>
              {p.name}
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <meta.icon className="w-4 h-4 text-[#00579e]" />
          <h3 className="cs-heading text-base font-semibold tracking-tight">{meta.label}</h3>
          {data && <span className="text-xs text-muted-foreground">· {data.rows.length} entries</span>}
        </div>
        {meta.next && <div className="text-xs text-muted-foreground">Drill into a row to see {meta.next} →</div>}
      </div>

      {/* Card grid */}
      {loading ? (
        <div className="cs-card p-10 text-center text-muted-foreground">Loading…</div>
      ) : currentLevel === "service" ? (
        <div className="cs-card p-5" data-testid="hierarchy-service-card">
          <h4 className="cs-heading text-sm font-semibold mb-3">Cost split by GCP service for this application</h4>
          <div className="space-y-2">
            {data.rows.map(r => {
              const total = data.rows.reduce((s, x) => s + x.total_cost_usd, 0) || 1;
              const pct = (r.total_cost_usd / total * 100).toFixed(1);
              return (
                <div key={r.id} className="flex items-center gap-3" data-testid={`svc-${r.name}`}>
                  <div className="w-32 shrink-0 text-xs text-foreground">{r.name}</div>
                  <div className="flex-1 h-6 bg-[hsl(var(--cs-tint))] rounded-md relative overflow-hidden">
                    <div className="h-full rounded-md flex items-center justify-end pr-2"
                         style={{ width: `${pct}%`, background: SERVICE_COLORS[r.name] || "#6b7280", minWidth: "60px" }}>
                      <span className="text-[10px] font-semibold text-white">{pct}%</span>
                    </div>
                  </div>
                  <div className="w-24 text-right cs-mono text-sm font-semibold shrink-0">{fmtUSD(r.total_cost_usd)}</div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {data?.rows.map(r => {
            const margin = r.margin_pct ?? 0;
            const marginColor = margin >= 30 ? "#15803d" : margin >= 15 ? "#f59e0b" : margin >= 0 ? "#9a3412" : "#b91c1c";
            return (
              <button key={r.id} onClick={() => drillInto(r)} disabled={!r.drillable}
                      data-testid={`hierarchy-card-${r.id}`}
                      className="cs-card p-5 text-left transition-all hover:border-[#00579e]/40 group">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                      {currentLevel.replace("_", " ")}
                    </div>
                    <h4 className="cs-heading text-base font-semibold tracking-tight mt-0.5 truncate">{r.name}</h4>
                  </div>
                  {r.drillable && <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-[#00579e] shrink-0 mt-1" />}
                </div>

                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Cost</div>
                    <div className="cs-mono font-semibold text-foreground mt-0.5">{fmtUSD(r.total_cost_usd)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Revenue</div>
                    <div className="cs-mono font-semibold text-foreground mt-0.5">{fmtUSD(r.total_revenue_usd || 0)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Margin</div>
                    <div className="cs-mono font-semibold mt-0.5" style={{ color: marginColor }}>
                      {r.margin_pct >= 0 ? "" : ""}{r.margin_pct}%
                    </div>
                  </div>
                </div>

                {(r.customer_count !== undefined || r.product_count !== undefined) && (
                  <div className="mt-3 pt-3 border-t border-border flex gap-3 text-[11px] text-muted-foreground">
                    {r.customer_count !== undefined && <span>{r.customer_count} customers</span>}
                    {r.product_count !== undefined && <span>· {r.product_count} products</span>}
                    {r.primary_services && <span className="truncate">· {r.primary_services.join(", ")}</span>}
                  </div>
                )}

                {/* Color bar */}
                <div className="mt-3 h-1 rounded-full" style={{ background: r.color || "#00579e" }} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
